import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PLANES, useSearch } from "../../../contexts/SearchContext";
import { useAppContext } from "../../../contexts/AppContext";
import { adaptDealToFlightOffer } from "../adapters/flightOfferAdapter";
import { computeFlightBookingSearchGate } from "../flightBookingSearchGate";
import { useBookingFlow } from "./hooks/useBookingFlow";
import { cheapestOfferIdsByPerPerson } from "./cheapestOfferIds";
import {
  buildSearchOfferDashboardPath,
  scrollSearchOfferFlightFlowToTop,
} from "../flightBookingPathUtils";
import {
  buildTaggedDealsForResults,
  countOffersByRouteKind,
  extractFlightOfferTraits,
  defaultFlightResultsFilterState,
  flightOfferMatchesFilters,
  sortFlightOffers,
  computePriceAndDurationBounds,
} from "./flightResultsFilterUtils";
import FlightResultsScreen from "../../../features/flightEngineV2/results/FlightResultsScreen";
import {
  buildResultsWave1aRegistry,
  logResultsWave1aParity,
  resolveRawOfferFromRegistry,
} from "../../../features/flightEngineV2/bridge/resultsWave1a";
import { buildGreenfieldSearchViewModel } from "../../../features/flightEngineV2/bridge/buildGreenfieldSearchViewModel";
import {
  buildGreenfieldFiltersViewModel,
  greenfieldFilterActionToPatch,
} from "../../../features/flightEngineV2/bridge/buildGreenfieldFiltersViewModel";
import {
  GREENFIELD_DATE_STRIP_PAGE_DAYS,
  buildGreenfieldDateStrip,
  returnDateKeepingNights,
  shiftGreenfieldStripDate,
} from "../../../features/flightEngineV2/bridge/buildGreenfieldDateStrip";
import { useFlightOfferUi } from "../../../contexts/FlightOfferUiContext";
import { requestMainFlightSearchEditor } from "../../../features/searchOffer/searchOfferEditSearchHandshake";

export default function ResultsStepCard() {
  const dragRef = useRef({
    isPointerDown: false,
    startX: 0,
    startY: 0,
    dragged: false,
  });
  const DRAG_THRESHOLD_PX = 5;
  const { isMobile, airlines } = useAppContext();
  const {
    availableFlights,
    loading,
    isSearched,
    error: searchContextError,
    setSavedDeal,
    roomInfo,
    origin,
    location,
    calendarDateArrival,
    calendarDateLeave,
    flightTripType,
    parallelRoundTripFlightSearchCompleted,
    roundTripFlightResults,
    outboundOneWayFlightResults,
    inboundOneWayFlightResults,
    setSelectedSelfComposeOutboundDeal,
    setSelectedSelfComposeInboundDeal,
    planesFlightSearchOutcome,
    flightAlternativeDateSuggestions,
    flightAlternativesProbeLoading,
    applyFlightAlternativeSuggestion,
    submitSearch,
    calculateLDSDealFareForRoomsComposition,
    searchTab,
    setSearchTab,
  } = useSearch();
  const { setFlightSearchEditorOpen, setImmersiveFlightResults } = useFlightOfferUi();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const routeParams = useParams();
  const {
    selectFlightOfferAndAdvanceBooking,
    selectedFlightOffer,
    currentStep,
    flowType,
  } = useBookingFlow();

  const gate = useMemo(
    () =>
      computeFlightBookingSearchGate({
        flightTripType,
        parallelRoundTripFlightSearchCompleted,
        roundTripFlightResults,
        outboundOneWayFlightResults,
        inboundOneWayFlightResults,
      }),
    [
      flightTripType,
      parallelRoundTripFlightSearchCompleted,
      roundTripFlightResults,
      outboundOneWayFlightResults,
      inboundOneWayFlightResults,
    ]
  );

  const taggedDeals = useMemo(
    () =>
      buildTaggedDealsForResults({
        gate,
        roundTripFlightResults,
        outboundOneWayFlightResults,
        inboundOneWayFlightResults,
        availableFlights,
      }),
    [
      gate,
      roundTripFlightResults,
      outboundOneWayFlightResults,
      inboundOneWayFlightResults,
      availableFlights,
    ]
  );

  const routeCounts = useMemo(
    () => countOffersByRouteKind(taggedDeals),
    [taggedDeals]
  );

  const baseOffers = useMemo(() => {
    return taggedDeals
      .map((row, index) => {
        const priced = calculateLDSDealFareForRoomsComposition(row.deal);
        const offer = adaptDealToFlightOffer(row.deal, index, roomInfo, {
          ourPrice: priced,
        });
        if (!offer) return null;
        return row.routeKind != null
          ? { ...offer, parallelRouteKind: row.routeKind }
          : offer;
      })
      .filter(Boolean);
  }, [taggedDeals, roomInfo, calculateLDSDealFareForRoomsComposition]);

  const traitsByOfferId = useMemo(() => {
    const m = new Map();
    for (const o of baseOffers) {
      const t = extractFlightOfferTraits(o.rawDeal, o);
      m.set(String(o.id), t);
    }
    return m;
  }, [baseOffers]);

  const traitsList = useMemo(
    () => Array.from(traitsByOfferId.values()),
    [traitsByOfferId]
  );

  const bounds = useMemo(
    () => computePriceAndDurationBounds(baseOffers, traitsByOfferId),
    [baseOffers, traitsByOfferId]
  );

  const searchFingerprint = useMemo(
    () =>
      `${taggedDeals.length}_${taggedDeals[0]?.deal?.dealId ?? taggedDeals[0]?.deal?.ID ?? ""}_${gate.rtCount}_${gate.outboundCount}_${gate.inboundCount}`,
    [taggedDeals, gate]
  );

  const [filterState, setFilterState] = useState(defaultFlightResultsFilterState);
  const [sortMode, setSortMode] = useState("cheapest");
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setFilterState(defaultFlightResultsFilterState());
    setSortMode("cheapest");
    setDrawerOpen(false);
  }, [searchFingerprint]);

  const setFilter = useCallback((patch) => {
    setFilterState((prev) => ({ ...prev, ...patch }));
  }, []);

  const onClearFilters = useCallback(() => {
    setFilterState(defaultFlightResultsFilterState());
  }, []);

  const handleCardMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    dragRef.current.isPointerDown = true;
    dragRef.current.startX = e.clientX;
    dragRef.current.startY = e.clientY;
    dragRef.current.dragged = false;
  }, []);

  const handleCardMouseMove = useCallback((e) => {
    if (!dragRef.current.isPointerDown) return;
    const dx = Math.abs(e.clientX - dragRef.current.startX);
    const dy = Math.abs(e.clientY - dragRef.current.startY);
    if (dx > DRAG_THRESHOLD_PX || dy > DRAG_THRESHOLD_PX) {
      dragRef.current.dragged = true;
    }
  }, []);

  const handleCardMouseUp = useCallback(() => {
    dragRef.current.isPointerDown = false;
  }, []);

  const filteredOffers = useMemo(() => {
    return baseOffers.filter((o) => {
      const tr = traitsByOfferId.get(String(o.id));
      if (!tr) return true;
      return flightOfferMatchesFilters(tr, filterState);
    });
  }, [baseOffers, traitsByOfferId, filterState]);

  const offers = useMemo(
    () => sortFlightOffers(filteredOffers, traitsByOfferId, sortMode),
    [filteredOffers, traitsByOfferId, sortMode]
  );

  const showRouteFilter = useMemo(
    () => taggedDeals.some((d) => d.routeKind != null),
    [taggedDeals]
  );

  const cheapestIds = useMemo(
    () => cheapestOfferIdsByPerPerson(offers),
    [offers]
  );

  const airlineNameMap = useMemo(() => {
    const m = new Map();
    for (const row of airlines || []) {
      const code = String(row?.id || row?.code || row?.IATA || "").trim();
      const name = String(row?.name || row?.Name || row?.heName || "").trim();
      if (code) m.set(code, name || code);
    }
    return m;
  }, [airlines]);

  const resultsWave1aRegistry = useMemo(
    () =>
      buildResultsWave1aRegistry(offers, {
        cheapestIds,
        airlineNameMap,
      }),
    [offers, cheapestIds, airlineNameMap]
  );

  const greenfieldSearchVm = useMemo(
    () =>
      buildGreenfieldSearchViewModel({
        origin,
        location,
        calendarDateArrival,
        calendarDateLeave,
        roomInfo,
        flightTripType,
      }),
    [
      origin,
      location,
      calendarDateArrival,
      calendarDateLeave,
      roomInfo,
      flightTripType,
    ]
  );

  const greenfieldFiltersVm = useMemo(
    () =>
      buildGreenfieldFiltersViewModel({
        filterState,
        traitsList,
        bounds,
        airlinesCatalog: airlines,
      }),
    [filterState, traitsList, bounds, airlines]
  );

  const greenfieldDateStrip = useMemo(
    () =>
      buildGreenfieldDateStrip({
        departDateLabel: calendarDateArrival,
        returnDateLabel: calendarDateLeave,
        suggestions: flightAlternativeDateSuggestions,
      }),
    [
      calendarDateArrival,
      calendarDateLeave,
      flightAlternativeDateSuggestions,
    ]
  );

  const onGreenfieldFilterAction = useCallback(
    (action) => {
      if (action?.type === "clear") {
        onClearFilters();
        return;
      }
      const patch = greenfieldFilterActionToPatch(action, filterState, bounds);
      if (!patch) return;
      if (patch.__replace) {
        onClearFilters();
        return;
      }
      setFilter(patch);
    },
    [filterState, bounds, onClearFilters, setFilter]
  );

  const onGreenfieldEditSearch = useCallback(() => {
    requestMainFlightSearchEditor();
    setImmersiveFlightResults(false);
    setFlightSearchEditorOpen(false);
    if (searchTab !== PLANES && typeof setSearchTab === "function") {
      setSearchTab(PLANES, {
        source: "edit_search_keep_fields",
        preserveSearchParams: true,
      });
    }
    const target = buildSearchOfferDashboardPath(routeParams);
    if (pathname !== target) navigate(target);
  }, [
    setImmersiveFlightResults,
    setFlightSearchEditorOpen,
    searchTab,
    setSearchTab,
    routeParams,
    pathname,
    navigate,
  ]);

  const searchDatesForDeparture = useCallback(
    (depDate) => {
      if (!depDate) return null;
      const isOneWay = /one.?way/i.test(String(flightTripType || ""));
      if (isOneWay) return { depDate };
      const retDate = returnDateKeepingNights(
        depDate,
        calendarDateArrival,
        calendarDateLeave
      );
      if (!retDate) return null;
      return { depDate, retDate };
    },
    [flightTripType, calendarDateArrival, calendarDateLeave]
  );

  const onGreenfieldSelectDateDay = useCallback(
    (day) => {
      const next = searchDatesForDeparture(day?.suggestion?.depDate);
      if (!next) return;
      applyFlightAlternativeSuggestion(next);
      scrollSearchOfferFlightFlowToTop();
    },
    [searchDatesForDeparture, applyFlightAlternativeSuggestion]
  );

  const onGreenfieldShiftDateDays = useCallback(
    (direction) => {
      const selected =
        greenfieldDateStrip.find((day) => day.selected) ?? null;
      const dep = selected?.suggestion?.depDate || calendarDateArrival;
      const delta =
        direction === "next"
          ? GREENFIELD_DATE_STRIP_PAGE_DAYS
          : direction === "prev"
            ? -GREENFIELD_DATE_STRIP_PAGE_DAYS
            : 0;
      if (!delta) return;
      const nextDep = shiftGreenfieldStripDate(dep, delta);
      const next = searchDatesForDeparture(nextDep);
      if (!next) return;
      applyFlightAlternativeSuggestion(next);
      scrollSearchOfferFlightFlowToTop();
    },
    [
      greenfieldDateStrip,
      calendarDateArrival,
      searchDatesForDeparture,
      applyFlightAlternativeSuggestion,
    ]
  );

  useEffect(() => {
    logResultsWave1aParity(offers, resultsWave1aRegistry);
  }, [offers, resultsWave1aRegistry]);

  useEffect(() => {
    if (typeof import.meta === "undefined" || !import.meta.env?.DEV) return;

    const availableFlightsLength =
      availableFlights == null
        ? 0
        : Array.isArray(availableFlights)
          ? availableFlights.length
          : 1;
    const roundTripFlightResultsLength = Array.isArray(roundTripFlightResults)
      ? roundTripFlightResults.length
      : 0;

    let emptyStateReason = "none";
    if (loading) emptyStateReason = "loading";
    else if (planesFlightSearchOutcome === "error") {
      emptyStateReason = "planes_outcome_error";
    } else if (planesFlightSearchOutcome === "empty") {
      emptyStateReason = "planes_outcome_empty";
    } else if (baseOffers.length === 0) {
      emptyStateReason = "no_offers_built";
    }

    console.log("[FLIGHTS_LOADING_STATE_AUDIT]", {
      source: "ResultsStepCard",
      loading,
      isSearched,
      error: Boolean(searchContextError),
      availableFlightsLength,
      roundTripFlightResultsLength,
      planesFlightSearchOutcome,
      currentStep,
      pathname: pathname || "",
    });

    console.log("[RESULTS_STEP_CARD_DATA_AUDIT]", {
      mounted: true,
      offersLength: offers.length,
      filteredOffersLength: filteredOffers.length,
      loading,
      error: Boolean(searchContextError),
      emptyStateReason,
    });

  }, [
    loading,
    isSearched,
    searchContextError,
    availableFlights,
    roundTripFlightResults,
    planesFlightSearchOutcome,
    offers.length,
    filteredOffers.length,
    currentStep,
    pathname,
    baseOffers.length,
  ]);

  const pickOffer = useCallback(
    (o) => {
      setSelectedSelfComposeOutboundDeal(null);
      setSelectedSelfComposeInboundDeal(null);
      const raw = o.rawDeal;
      if (raw) {
        setSavedDeal(raw);
      }
      selectFlightOfferAndAdvanceBooking(o);
    },
    [
      selectFlightOfferAndAdvanceBooking,
      setSavedDeal,
      setSelectedSelfComposeInboundDeal,
      setSelectedSelfComposeOutboundDeal,
    ]
  );

  const onSelectOfferIdFromV2 = useCallback(
    (offerId) => {
      const raw = resolveRawOfferFromRegistry(resultsWave1aRegistry, offerId);
      if (!raw) return;
      void pickOffer(raw);
    },
    [resultsWave1aRegistry, pickOffer]
  );

  useEffect(() => {
    if (typeof import.meta === "undefined" || !import.meta.env?.DEV) return;
    if (loading || baseOffers.length === 0) return;
    console.log("[FLIGHT_RESULTS_FILTER_LAYOUT_AUDIT]", {
      viewportWidth:
        typeof window !== "undefined" && typeof window.innerWidth === "number"
          ? window.innerWidth
          : null,
      filtersPlacement: isMobile ? "drawer" : "top_bar",
      resultsCount: offers.length,
      isMobile,
    });
  }, [loading, baseOffers.length, offers.length, isMobile]);

  const statusContent = (
    <>
      {!loading &&
        gate.partialSelfCompose &&
        !gate.outboundOnlyNoReturn && (
        <p className="fw-ui-results-hint fw-ui-warning-text">
          לא נמצאה טיסת RT מוכנה, וחסר כיוון אחד בלבד (
          {gate.outboundCount === 0 ? "אין הלוך" : "אין חזור"}). אין המשך
          ל־Extras — נסו תאריכים אחרים או יעד אחר.
        </p>
      )}
      {!loading && gate.needsSelfComposePickStep && (
        <p className="fw-ui-results-hint fw-ui-selfcompose-results-hint">
          לא נמצאה טיסת הלוך-חזור מוכנה. בשלב הבא תבחרו הלוך וחזור מנפרד,
          ואז תלחצו &quot;המשך עם המסלול שבניתי&quot; לפני Extras.
        </p>
      )}
      {!loading && planesFlightSearchOutcome === "error" && (
        <div className="fb-flight-search-error-banner" role="alert">
          אירעה שגיאה בעת חיפוש הטיסות, נא לנסות שוב
        </div>
      )}
      {!loading && planesFlightSearchOutcome === "empty" && (
        <div className="fb-flight-empty-state">
          <h3 className="fb-flight-empty-state__title">
            לא נמצאו טיסות לתאריכים והיעד שבחרת
          </h3>
          <p className="fb-flight-empty-state__hint">
            נסה לשנות תאריכים, יעד, או מספר נוסעים כדי למצוא אפשרויות נוספות
          </p>
          {flightAlternativesProbeLoading ? (
            <p className="fb-flight-empty-state__probe" role="status">
              בודקים תאריכים קרובים…
            </p>
          ) : null}
          {!flightAlternativesProbeLoading &&
            flightAlternativeDateSuggestions.length > 0 ? (
            <div className="fb-flight-empty-state__alternatives">
              <p className="fb-flight-empty-state__alt-title">
                נמצאו תוצאות בתאריכים קרובים
              </p>
              <div className="fb-flight-empty-state__chips">
                {flightAlternativeDateSuggestions.map((row, idx) => (
                  <button
                    key={`${row.depDate}-${row.retDate ?? "ow"}-${idx}`}
                    type="button"
                    className="fb-flight-alt-chip"
                    onClick={() => {
                      applyFlightAlternativeSuggestion(row);
                      scrollSearchOfferFlightFlowToTop();
                    }}
                  >
                    {row.retDate
                      ? `יציאה ${row.depDate}, חזרה ${row.retDate}`
                      : `יציאה ${row.depDate}`}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <button
            type="button"
            className="fb-flight-empty-state__retry"
            onClick={() => {
              void submitSearch({ source: "button" });
              scrollSearchOfferFlightFlowToTop();
            }}
          >
            חיפוש מחדש
          </button>
        </div>
      )}
      {!loading &&
        (!gate.partialSelfCompose || gate.outboundOnlyNoReturn) &&
        !gate.needsSelfComposePickStep &&
        baseOffers.length === 0 &&
        planesFlightSearchOutcome !== "empty" &&
        planesFlightSearchOutcome !== "error" && (
        <p className="fw-ui-results-empty">
          אין תוצאות עדיין — הריצו חיפוש בשורת החיפוש (טיסות), או השתמשו בכפתור
          mock למטה.
        </p>
      )}
    </>
  );

  return (
    <>
      <FlightResultsScreen
        offers={resultsWave1aRegistry.greenfieldOffers}
        search={greenfieldSearchVm}
        dateStrip={greenfieldDateStrip}
        filters={greenfieldFiltersVm}
        sortMode={
          sortMode === "cheapest" ||
          sortMode === "fastest" ||
          sortMode === "recommended"
            ? sortMode
            : "cheapest"
        }
        onSortChange={setSortMode}
        selectedOfferId={selectedFlightOffer?.id ?? null}
        onSelectOfferId={onSelectOfferIdFromV2}
        onEditSearch={onGreenfieldEditSearch}
        onFilterAction={onGreenfieldFilterAction}
        onSelectDateDay={onGreenfieldSelectDateDay}
        onShiftDateDays={onGreenfieldShiftDateDays}
        loading={loading}
        statusContent={statusContent}
        resultCount={offers.length}
      />
    </>
  );
}
